import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

let isReconcilingFreeGift = false;
let hasQueuedEligibilityCheck = false;
let queuedCartResponse;

/** @param {unknown} cart */
function isCartResponse(cart) {
  return Boolean(cart && typeof cart === 'object' && Array.isArray(cart.items) && typeof cart.total_price === 'number');
}

/** Removes claimed gifts when the qualifying cart value no longer meets the threshold. */
async function reconcileFreeGiftEligibility(cartFromEvent) {
  const policy = document.querySelector('[data-free-gift-policy]');
  const threshold = Number(policy?.dataset.threshold);
  if (!threshold) return;
  if (isReconcilingFreeGift) {
    hasQueuedEligibilityCheck = true;
    queuedCartResponse = cartFromEvent;
    return;
  }

  isReconcilingFreeGift = true;
  try {
    // Quantity changes provide the updated cart in the theme event, avoiding an
    // unnecessary request. Product additions fall back to Shopify's cart API.
    let cart = cartFromEvent;
    if (!isCartResponse(cart)) {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Unable to check free gift eligibility.');
      cart = await cartResponse.json();
    }

    const gifts = cart.items.filter((item) => item.properties?._free_gift === 'true');
    if (!gifts.length) return;

    // Gift lines never contribute toward qualifying for their own promotion.
    const giftValue = gifts.reduce((total, item) => total + item.final_line_price, 0);
    if (cart.total_price - giftValue >= threshold) return;

    const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const updates = Object.fromEntries(gifts.map((item) => [item.key, 0]));
    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', {
        body: JSON.stringify({ updates, sections: sections.join(','), sections_url: window.location.pathname }),
      })
    );
    const data = await response.json();
    if (!response.ok || data.status) throw new Error('Unable to remove ineligible free gift.');

    document.dispatchEvent(
      new CartUpdateEvent(data, 'free-gift-eligibility', {
        source: 'free-gift-eligibility',
        itemCount: data.item_count,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('Free gift eligibility update failed.', error);
  } finally {
    isReconcilingFreeGift = false;
    if (hasQueuedEligibilityCheck) {
      const nextCartResponse = queuedCartResponse;
      hasQueuedEligibilityCheck = false;
      queuedCartResponse = undefined;
      void reconcileFreeGiftEligibility(nextCartResponse);
    }
  }
}

function scheduleFreeGiftEligibilityCheck(event) {
  // The removal request below emits its own update event. It is already valid,
  // so skip it rather than checking the cart a second time.
  if (event.detail?.data?.source === 'free-gift-eligibility') return;
  void reconcileFreeGiftEligibility(event.detail?.resource);
}

// Covers every theme cart mutation: add, quantity change, remove, and cart-page updates.
document.addEventListener(ThemeEvents.cartUpdate, scheduleFreeGiftEligibilityCheck);
// Discount changes can alter the cart total without emitting cart:update.
document.addEventListener(ThemeEvents.discountUpdate, scheduleFreeGiftEligibilityCheck);

/** A free-gift picker that uses Shopify's AJAX cart API and theme cart refresh events. */
class CartFreeGift extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleVariantChange);
    this.addEventListener('keydown', this.#handleKeydown);
    this.querySelector('[data-free-gift-dialog]')?.addEventListener('cancel', this.#handleCancel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleVariantChange);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.querySelector('[data-free-gift-dialog]')?.removeEventListener('cancel', this.#handleCancel);
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const dialog = target.closest('[data-free-gift-dialog]');
    // A click on a native dialog's backdrop has the dialog itself as its target.
    // Stop it reaching the cart drawer, otherwise both drawers can start closing
    // and the still-open gift dialog blocks all subsequent clicks.
    if (dialog instanceof HTMLDialogElement && target === dialog) {
      event.stopPropagation();
      const { left, right, top, bottom } = dialog.getBoundingClientRect();
      const clickedBackdrop = event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom;
      if (clickedBackdrop) this.#closeDrawer();
      return;
    }

    if (target.closest('[data-free-gift-open]')) {
      const giftDialog = this.querySelector('[data-free-gift-dialog]');
      // This needs to be a top-layer dialog so it sits above the cart contents.
      if (giftDialog instanceof HTMLDialogElement && !giftDialog.open) giftDialog.showModal();
      return;
    }
    if (target.closest('[data-free-gift-close]')) return this.#closeDrawer();

    const filter = target.closest('[data-free-gift-filter]');
    if (filter instanceof HTMLButtonElement) return this.#filterGifts(filter);

    const product = target.closest('[data-free-gift-product]');
    if (product instanceof HTMLElement) return this.#selectGift(product);

    const button = target.closest('[data-free-gift-add]');
    if (button instanceof HTMLButtonElement) this.#addGift(button);
  };

  #handleVariantChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-free-gift-variant]')) return;
    const product = select.closest('[data-free-gift-product]');
    if (!(product instanceof HTMLElement)) return;
    product.dataset.variantId = select.value;
    this.#selectGift(product);
  };

  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.querySelector('[data-free-gift-dialog]')?.open) {
      event.preventDefault();
      event.stopPropagation();
      this.#closeDrawer();
      return;
    }
    const product = event.target instanceof Element ? event.target.closest('[data-free-gift-product]') : null;
    if (!(product instanceof HTMLElement) || event.target !== product || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    this.#selectGift(product);
  };

  #handleCancel = (event) => {
    if (!(event.target instanceof HTMLDialogElement) || !event.target.matches('[data-free-gift-dialog]')) return;
    event.preventDefault();
    this.#closeDrawer();
  };

  #closeDrawer() {
    const dialog = this.querySelector('[data-free-gift-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open || dialog.dataset.closing !== undefined) return;
    dialog.dataset.closing = '';
    window.setTimeout(() => {
      dialog.close();
      delete dialog.dataset.closing;
      this.querySelector('[data-free-gift-open]')?.focus();
    }, 280);
  }

  #selectGift(product) {
    const addButton = this.querySelector('[data-free-gift-add]');
    if (!(addButton instanceof HTMLButtonElement)) return;

    for (const item of this.querySelectorAll('[data-free-gift-product]')) {
      item.setAttribute('aria-checked', String(item === product));
    }

    const variantSelect = product.querySelector('select');
    const unavailable = variantSelect instanceof HTMLSelectElement && variantSelect.selectedOptions[0]?.disabled;
    addButton.dataset.variantId = product.dataset.variantId || '';
    addButton.disabled = !product.dataset.variantId || unavailable;
  }

  #filterGifts(filter) {
    const category = filter.dataset.freeGiftFilter || 'all';
    for (const button of this.querySelectorAll('[data-free-gift-filter]')) {
      const active = button === filter;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
    for (const product of this.querySelectorAll('[data-free-gift-product]')) {
      product.hidden = category !== 'all' && product.dataset.category !== category;
    }
  }

  async #addGift(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const error = this.querySelector('[data-free-gift-error]');
    button.disabled = true;
    button.dataset.loading = 'true';
    if (error instanceof HTMLElement) error.hidden = true;

    const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(this.dataset.errorMessage || 'Unable to add the gift.');

      this.dispatchEvent(new CartAddEvent(data, variantId, {
        source: 'cart-free-gift',
        itemCount: data.item_count,
        variantId,
        sections: data.sections,
      }));
    } catch (exception) {
      if (error instanceof HTMLElement) {
        error.textContent = this.dataset.errorMessage || 'Unable to add the gift.';
        error.hidden = false;
      }
      delete button.dataset.loading;
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-free-gift')) customElements.define('cart-free-gift', CartFreeGift);
